<template>
  <div class="shop-widget">
    <align class="header" flex>
      <align>
        <h3 class="secondary-font">
          Shop
        </h3>

        <saved-filters
          hide-save
          type="shop"
          align="left"
          allow-deselect
          :query="activeSavedFilter ? activeSavedFilter.query : ''"
          :filters="activeSavedFilter ? activeSavedFilter.filters : {}"
          :is-filtering="!!activeSavedFilter"
          @select="selectSavedFilter"
        />
      </align>

      <icon-link @click.native="viewShop">
        View More
      </icon-link>
    </align>

    <p class="affiliate-text">
      If you purchase an item from the results below after clicking the link, Card Ladder may be compensated at no additional cost to you.
    </p>

    <div v-if="items.length > 0">
      <div class="grid">
        <shop-listing-card
          v-for="item in items"
          :key="item.listingId"
          :item="item"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
        />
      </div>
    </div>

    <div v-else-if="!loading">
      There are no listings matching your saved search.
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';

import ShopListingCard from '@cardladder/shared/components/ShopListingCard';

import { authService, searchService } from '@/services';

import { LISTING_SORT_OPTIONS } from '@cardladder/shared/constants';

export default {
  components: {
    ShopListingCard,
  },

  data() {
    return {
      loaded: false,
      loading: true,
      items: [],
      listener: null,
      interval: null,
      savedFilters: [],
      savedFiltersLoaded: false,
    };
  },

  created() {
    this.interval = setInterval(this.load, 30000);

    this.listener = authService.watchSavedFilters({
      uid: this.user.uid,
      type: 'shop',
      onSnapshot: (snapshot) => {
        this.savedFilters = snapshot;
        this.savedFiltersLoaded = true;
      },
    });
  },

  beforeDestroy() {
    clearInterval(this.interval);
    this.listener();
  },

  watch: {
    activeSavedFilter: {
      handler() {
        this.load();
      },
      immediate: true,
    },

    savedFiltersLoaded: {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    shopWidgetConfig() {
      return this.user.dashboardWidgets.find(({ component }) => component === 'ShopWidget');
    },

    activeSavedFilter: {
      get() {
        const savedFilterId = this.shopWidgetConfig.savedFilterId;

        return this.savedFilters.find(({ id }) => id === savedFilterId);
      },

      async set(savedFilter) {
        await authService.updateUser(this.user.uid, {
          dashboardWidgets: [
            ...this.user.dashboardWidgets.map((item) => {
              if (item.component === 'ShopWidget') {
                return {
                  ...item,
                  savedFilterId: savedFilter?.id || null,
                };
              }

              return item;
            }),
          ],
        });
      },
    },
  },

  methods: {
    async load() {
      if (!this.savedFiltersLoaded) return;

      const sort = LISTING_SORT_OPTIONS.find((value) => value === this.$store.state.listingsSort) || LISTING_SORT_OPTIONS[0];
      const direction = this.$store.state.listingsDirection;

      try {
        const data = await searchService.search({
          index: 'listings',
          limit: 4,
          sort,
          direction,
          query: this.activeSavedFilter?.query || '',
          filters: {
            ...(this.activeSavedFilter?.filters || {}),
            endDate: 'now',
          },
        });

        this.items = data.hits;
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loading = false;
    },

    selectSavedFilter(savedFilter) {
      this.activeSavedFilter = savedFilter;
    },

    viewShop() {
      if (this.activeSavedFilter && this.activeSavedFilter.id) {
        this.$store.commit('SET_LISTINGS_FILTERS', this.activeSavedFilter.filters);
        this.$store.commit('SET_LISTINGS_QUERY', this.activeSavedFilter.query);
      }

      this.$router.push('/shop');
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  margin: 0 0 $gp * .5;
}

h3 {
  margin: 0;
}

.grid {
  @include grid-panel;
}

.list {
  margin: 0 (-$gp);
}
</style>
